<template>
  <div class="ts-chart">
    <div class="ts-chart__legend">
      <span class="ts-chart__legend-item ts-chart__legend-item--ist">— IST</span>
      <span class="ts-chart__legend-item ts-chart__legend-item--plan">- - Plan</span>
      <span class="ts-chart__legend-item ts-chart__legend-item--vj">· · Vorjahr</span>
    </div>
    <apexchart
      type="line"
      height="280"
      :options="chartOptions"
      :series="series"
    />
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';

const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function fmtK(v) {
  if (v === null || v === undefined) return '';
  return `${Math.round(v / 1000)}k`;
}

function buildVorjahrByMonth(vorjahr) {
  const map = new Map();
  for (const row of vorjahr || []) {
    if (row?.monat !== null && row?.monat !== undefined) {
      map.set(Number(row.monat), row);
    }
  }
  return map;
}

export default {
  components: { apexchart: VueApexCharts },
  props: {
    months: { type: Array, required: true },
    vorjahr: { type: Array, default: () => [] },
    metric: { type: String, required: true },
    effectiveMonth: { type: Number, default: null },
    year: { type: Number, required: true },
  },
  computed: {
    istKey() { return `${this.metric}_ist`; },
    planKey() { return `${this.metric}_plan`; },

    series() {
      const vorjahrByMonth = buildVorjahrByMonth(this.vorjahr);
      const istData = this.months.map((m) => {
        if (this.effectiveMonth !== null && m.monat > this.effectiveMonth) return null;
        const val = m[this.istKey];
        return val !== null ? Number(val) : null;
      });
      const planData = this.months.map((m) => {
        const val = m[this.planKey];
        return val !== null ? Number(val) : null;
      });
      const vjData = this.months.map((m) => {
        const vj = vorjahrByMonth.get(Number(m.monat));
        const val = vj?.[this.istKey];
        return val !== null && val !== undefined ? Number(val) : null;
      });
      return [
        { name: 'IST', type: 'area', data: istData },
        { name: 'Plan', type: 'line', data: planData },
        { name: 'Vorjahr', type: 'line', data: vjData },
      ];
    },

    chartOptions() {
      return {
        chart: {
          id: 'timeseries',
          type: 'line',
          toolbar: { show: false },
          animations: { enabled: false },
          fontFamily: 'inherit',
          background: 'transparent',
        },
        dataLabels: { enabled: false },
        // IST: kräftig blau · Plan: helleres blau (gestrichelt) · VJ: sehr dezent
        colors: ['#23799D', '#4DAED7', '#B7B9BE'],
        stroke: {
          width: [3, 1.5, 1.5],
          dashArray: [0, 7, 4],
          curve: 'smooth',
        },
        fill: {
          type: ['gradient', 'solid', 'solid'],
          gradient: {
            type: 'vertical',
            shadeIntensity: 0,
            opacityFrom: 0.15,
            opacityTo: 0,
            stops: [0, 100],
          },
        },
        markers: {
          size: [0, 0, 0],
          hover: { size: 5 },
        },
        xaxis: {
          categories: MONTH_LABELS,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: '#6B6E76', fontSize: '12px' } },
        },
        yaxis: {
          labels: {
            formatter: fmtK,
            style: { colors: '#6B6E76', fontSize: '12px' },
          },
        },
        annotations: { yaxis: [] },
        grid: {
          borderColor: '#0B120E24',
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
        },
        tooltip: {
          y: {
            formatter: (v) => v !== null
              ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
              : '–',
          },
        },
        legend: { show: false },
      };
    },
  },
};
</script>

<style scoped>
.ts-chart {
  padding: 12px 16px 0;
}

.ts-chart__legend {
  display: flex;
  gap: 16px;
  font-size: 0.75rem;
  color: var(--ds-text-subtle);
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.ts-chart__legend-item--ist  { color: #23799D; font-weight: 800; letter-spacing: 0.01em; }
.ts-chart__legend-item--plan { color: #23799D; font-weight: 400; }
.ts-chart__legend-item--vj   { color: #8C8F97; font-weight: 400; }
</style>
